import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { useLogger, usePrettyLogEventPage } from "@components/v2/analytics";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { DATE_RANGE_CHOICE_LABELS } from "@features/sales-v3/top-level-filters/components/date-filters";
import { dateRangeOptionsForTimeGranularity } from "@north-beam/nb-common";
import { FlatSelect, type FlatSelectOption } from "@shared/flat-select";
import { DateRangeCalendarSelect } from "@shared/selects/date-range-calendar-select";

type Props = {
  className?: string;
  isFromMinimizedTopBar?: boolean;
};

export const DateFilters = ({ className }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const logger = useLogger({ container: "date-ranges" });

  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      dateRange,
      dateRangeInterval,
      compareDateRange,
      level,
    },
    setDateRange,
  } = useOverviewContext();

  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    level,
  };

  const [selected, setSelected] = useState<FlatSelectOption<string>>({
    label: "",
    value: "",
  });

  useEffect(() => {
    if (typeof dateRange !== "string") {
      return setSelected({
        label: "",
        value: "custom",
      });
    }
    setSelected({
      label: "",
      value: dateRange,
    });
  }, [dateRange]);

  const options = useMemo(() => {
    return [
      {
        label: "",
        value: "custom",
        renderOption: ({ isSelected }) => (
          <DateRangeCalendarSelect
            calendarClassName="-ml-1 mt-0.5"
            value={dateRangeInterval}
            onApply={(v) => {
              setDateRange(v);
              logger.event.overviewPageSelectCustomDateRange("click", {
                eventDetails: {
                  ...v,
                },
              });
            }}
            buttonComponent={(ref, refProps) => (
              <div
                ref={ref}
                {...refProps}
                className={clsx(" h-full flex items-center cursor-pointer", {
                  "text-white": isSelected,
                })}
              >
                Custom
              </div>
            )}
          />
        ),
      },
      ...dateRangeOptionsForTimeGranularity(timeGranularity).map((e) => {
        return {
          label: DATE_RANGE_CHOICE_LABELS[e.value],
          value: e.value,
          onClick: () => {
            const newPredefinedDateRange = e.value;
            setDateRange(newPredefinedDateRange);

            prettyLogEventPage({
              domain: "Top Level Filters",
              action: "onChange",
              additionalActionInfo: "Set Date Range",
              data: {
                newDateRange: newPredefinedDateRange,
                pageContext,
              },
            });

            logger.event.overviewPageSelectPredefinedDateRange("click", {
              eventDetails: {
                shortcutDateRange: newPredefinedDateRange,
              },
            });
          },
        };
      }),
    ] as FlatSelectOption<string>[];
  }, [
    dateRangeInterval,
    logger.event,
    prettyLogEventPage,
    setDateRange,
    timeGranularity,
  ]);

  return (
    <FlatSelect
      className={className}
      options={options}
      setSelected={(v) => {
        setSelected(v);
      }}
      selected={selected}
    />
  );
};
